<template>
  <NavBar v-if="showNavbar" class="mol-nav-bar" :class="{'hide-unless-hovering': $screenshotMode && $screenshotModeSettings.hoverToRevealNavbar}" @log-out-user="signOut"></NavBar>
  <div class="mol-router-view" :class="{'showing-cookie-bar': showingCookieBar}">
    <router-view @user-updated="userUpdated"></router-view>
  </div>
  <FooterBar class="mol-footer-bar" :class="{'hide-unless-hovering': $screenshotMode, 'showing-cookie-bar': showingCookieBar}"></FooterBar>
  <CookieBar class="mol-cookie-bar" @visibility-update="onCookieBarVisibilityChange"></CookieBar>
  <ToastNotification v-if="toastNotificationsEnabled"></ToastNotification>
  <div v-if="watermarkText" class="mol-watermark" v-html="watermarkText"></div>
  <DebuggingComponent v-if="$screenshotMode" />
</template>

<script>

import NavBar from "@/components/NavBar.vue";
import axios from "axios";
import { userStore } from "@/userStore";
import ToastNotification from "@/components/toast notifications/ToastNotification.vue";
import FooterBar from "@/components/FooterBar.vue";
import analytics from "@/analytics";
import CookieBar from "@/components/CookieBar.vue";
import {Tooltip} from "bootstrap";
import DebuggingComponent from "@/components/DebuggingComponent.vue";

export default {
  name: 'App',
  components: {
    DebuggingComponent,
    FooterBar,
    CookieBar,
    ToastNotification,
    NavBar
  },
  data() {
    return {
      userStore,
      showingCookieBar: false
    }
  },
  computed: {
    showNavbar() {
      return !this.$route.meta.hideNavbar;
    },
    toastNotificationsEnabled() {

      // screenshot mode may disable toasts
      if ( this.$screenshotMode ) {
        return !this.$screenshotModeSettings.disableToastNotifications;
      }

      // enabled by default
      return true;
    },
    watermarkText() {

      if ( this.$screenshotMode ) {
        return null;
      }

      const environment = (this.$serverInfo?.environment === 'development')? 'dev' : this.$serverInfo?.environment;
      const database = this.$serverInfo?.database;

      // only show watermark for non-prod info
      const nonProdEnvironment = environment && (environment !== 'production');
      const nonProdDatabase = database && (database !== 'oscarlist');
      if ( nonProdEnvironment || nonProdDatabase ) {
        return `${environment}<br>server<br><span style="font-size: 50%;">${database}</span>`;
      }

      return null;
    }
  },
  mounted() {
    // add frontend version to each api request header
    axios.defaults.headers.common['x-mol-frontend-build-version'] = document?.documentElement?.dataset?.molFrontendBuildVersion;

    // create bootstrap tooltip handler
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
    });

    // set color theme to our preferred value, and listen for changes in 'prefers-color-scheme' to update our theme
    this.$colorTheme.setTheme(this.$colorTheme.getPreferredTheme());
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      this.$colorTheme.setTheme(this.$colorTheme.getPreferredTheme());
    });
  },
  methods: {
    userUpdated(_user) {
      userStore.setUser(_user);
    },
    signOut() {
      axios
          .post("/api/auth/logout")
          .then((/*response*/) => {
            // console.log("response:");
            // console.log(response);
            userStore.setUser(null);
            this.$router.push({name: 'WelcomePage'});
          }, (error) => {
            analytics.notifyBugsnagOfError(error);
            if ( error.response.data ) {
              alert(error.response.data);
            }
          })
    },
    onCookieBarVisibilityChange(_showing) {
      this.showingCookieBar = _showing;
    },
  }
}
</script>

<style>

  :root {
    --oscar-gold: #c7a662;

    --mol-color-gold: #e1bb28;
    --mol-color-gold-text: #b49823;
    --mol-color-gold-faded: #dec563;
    --mol-color-gold-background: #fffcf4;

    --mol-color-red-carpet: #C40404;
    --mol-color-red-carpet-slightly-faded: #c25f5f;
    --mol-color-red-carpet-faded: #eab7b7;
    --mol-color-red-carpet-background: #FCF2F2;

    --mol-color-win-icon: #b49823;
    --mol-standard-routerview-height: calc(100vh - calc(var(--mol-navbar-height) + var(--mol-footerbar-height)));

    --mol-friends-bg: aliceblue;
    --mol-friends-border-color: cornflowerblue;
    --mol-friends-color: cornflowerblue;


    /* https://design.chicago.gov/2019/08/26/choosing-the-cds-color-set.html */
    --mol-color-ccs-blue: #41B6E6;
    --mol-color-ccs-red: #E4002B;

    /* typical browser scrollbar width */
    --mol-scrollbar-width: 15px;
    @media (pointer: coarse) {
      /* touchscreens don't really have scrollbars--just a hovering internal one visible only while scrolling */
      --mol-scrollbar-width: 0px;
    }
  }

  [data-bs-theme=dark] {
    --mol-color-gold-text: #dec563;

    --mol-color-red-carpet-background: #2b1c1c;
    --mol-color-gold-background: #292621;

    --mol-friends-bg: #212533;
    --mol-friends-border-color: #36517e;
    --mol-friends-color: cornflowerblue;
  }


  body {
    /* add some top-padding to the body to account for the navbar */
    padding-top: var(--mol-navbar-height);
  }


  .form-switch .form-check-input {
    border-color: var(--bs-secondary-color);
    --bs-form-switch-bg: url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%27-4 -4 8 8%27%3e%3ccircle r=%273%27 fill=%27rgb%280, 0, 0%29%27/%3e%3c/svg%3e")!important;

    &:checked {
      background-color: var(--mol-color-gold-faded)!important;
      border-color: var(--mol-color-gold-faded)!important;
    }

    &:focus {
      box-shadow: 0 0 0 .25rem #dec56340;
    }
  }

  [data-bs-theme="dark"] .form-switch .form-check-input {
    --bs-form-switch-bg: url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%27-4 -4 8 8%27%3e%3ccircle r=%273%27 fill=%27rgb%28255, 255, 255%29%27/%3e%3c/svg%3e")!important;

    &:checked {
      --bs-form-switch-bg: url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%27-4 -4 8 8%27%3e%3ccircle r=%273%27 fill=%27rgb%280, 0, 0%29%27/%3e%3c/svg%3e")!important;
    }
  }


  .mol-router-view {

    /* debug */
    /*border: dashed red;*/

    min-height: var(--mol-standard-routerview-height);

    &.showing-cookie-bar {
      min-height: calc(var(--mol-standard-routerview-height) - var(--mol-cookiebar-height));
    }
  }

  .mol-footer-bar {
    &.showing-cookie-bar {
      margin-bottom: var(--mol-cookiebar-height);
    }
  }



  .admin-colors {
    color: crimson;
  }

  .mol-typical-page-container-10 {
    padding-top: 10px;
    &.showing-message-banner {
      padding-top: calc(10px + var(--mol-membership-banner-bar-height));
    }
  }
  .mol-typical-page-container-20 {
    padding-top: 20px;
    &.showing-message-banner {
      padding-top: calc(20px + var(--mol-membership-banner-bar-height));
    }
  }

  .mol-friend-colors {
    background-color: #f0f8ff!important;
    border-color: cornflowerblue!important;
  }
  [data-bs-theme="dark"] {
    .mol-friend-colors {
      background-color: #212533!important;
      border-color: #36517e!important;
    }
  }

  .mol-center-card-width {
    max-width: min(600px, calc(100vw - var(--bs-gutter-x)));
    margin-left: auto;
    margin-right: auto;
  }

  .btn-mol {
    --bs-btn-color: #fff!important;;
    --bs-btn-bg: var(--mol-color-gold)!important;
    --bs-btn-border-color: var(--mol-color-gold)!important;

    --bs-btn-hover-color: #fff;
    --bs-btn-hover-bg: var(--mol-color-win-icon)!important;
    --bs-btn-hover-border-color: var(--mol-color-gold)!important;

    --bs-btn-focus-shadow-rgb: 49, 132, 253;

    --bs-btn-active-color: #fff;
    --bs-btn-active-bg: var(--mol-color-gold)!important;
    --bs-btn-active-border-color: var(--mol-color-gold)!important;
    --bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);

    --bs-btn-disabled-color: #fff;
    --bs-btn-disabled-bg: var(--mol-color-gold)!important;
    --bs-btn-disabled-border-color: var(--mol-color-gold)!important;
  }

  .btn-outline-mol {
    --bs-btn-color: var(--mol-color-gold-text)!important;
    --bs-btn-border-color: var(--mol-color-gold-text)!important;

    --bs-btn-hover-color: #fff!important;
    --bs-btn-hover-bg: var(--mol-color-win-icon)!important;
    --bs-btn-hover-border-color: var(--mol-color-gold)!important;
    --bs-btn-focus-shadow-rgb: 13, 110, 253!important;

    --bs-btn-active-color: #fff!important;
    --bs-btn-active-bg: var(--mol-color-gold)!important;
    --bs-btn-active-border-color: var(--mol-color-gold)!important;
    --bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125)!important;

    --bs-btn-disabled-color: var(--mol-color-gold)!important;
    --bs-btn-disabled-bg: transparent!important;
    --bs-btn-disabled-border-color: var(--mol-color-gold)!important;

    --bs-gradient: none!important;
  }

  .mol-color-theme-oscar-pool {
    --bs-card-color: var(--mol-color-gold-text)!important;
    --bs-card-cap-color: var(--mol-color-gold-text)!important;
    --bs-card-bg: var(--mol-color-gold-background)!important;
    --bs-card-border: solid!important;
    --bs-card-border-width: 1px!important;
    --bs-card-border-color: var(--mol-color-gold)!important;
    --bs-card-border-radius: 0.375rem!important;

    color: var(--mol-color-gold-text);
  }

  .mol-color-theme-membership {
    --bs-card-color: var(--mol-color-gold-text)!important;
    --bs-card-cap-color: var(--mol-color-gold-text)!important;
    --bs-card-bg: var(--mol-color-gold-background)!important;
    --bs-card-border: solid!important;
    --bs-card-border-width: 1px!important;
    --bs-card-border-color: var(--mol-color-gold)!important;
    --bs-card-border-radius: 0.375rem!important;

    color: var(--mol-color-gold-text);
  }


  .mol-tooltip {

    /* OVERRIDABLE BOOTSTRAP VARIABLES
    --bs-tooltip-zindex
    --bs-tooltip-max-width
    --bs-tooltip-padding-x
    --bs-tooltip-padding-y
    --bs-tooltip-margin
    --bs-tooltip-color
    --bs-tooltip-bg
    --bs-tooltip-border-radius
    --bs-tooltip-opacity
    --bs-tooltip-arrow-width
    --bs-tooltip-arrow-height
     */

    --bs-tooltip-bg: #b49823!important;
    --bs-tooltip-color: var(--bs-body-bg)!important;
    --bs-tooltip-opacity: 1.0!important;
    --bs-tooltip-arrow-height: 1em!important;
    --bs-tooltip-max-width: 100%!important;
    font-size: x-small!important;
  }

  .mol-subtle-link {
    text-decoration: inherit;
    color: inherit;
    font-weight: 500;

    &:hover {
      color: var(--bs-link-hover-color);
      cursor: pointer;
    }
  }

  .mol-internal-link::after {
    vertical-align: super;
    font-size: 60%;
    margin-left: 0.01em;
    content: "\f0c1"; /* link (chain) */
    font-family: "FontAwesome";
  }

  .mol-external-link::after {
    vertical-align: super;
    font-size: 60%;
    margin-left: 0.1em;
    content: "\f08e"; /* external link */
    font-family: "FontAwesome";
  }

  .mol-red-ribbon {
    position: absolute;
    left: -5px; top: -5px;
    z-index: 1;
    overflow: hidden;
    width: 75px; height: 75px;
    text-align: right;
  }

  .mol-red-ribbon span {
    font-size: 10px;
    font-weight: bold;
    color: #FFF;
    text-transform: uppercase;
    text-align: center;
    line-height: 20px;
    transform: rotate(-45deg);
    -webkit-transform: rotate(-45deg);
    width: 100px;
    display: block;
    background: #D90505;
    background: linear-gradient(#D90505 0%, #C40404 100%);
    box-shadow: 0 3px 10px -5px rgba(0, 0, 0, 1);
    position: absolute;
    top: 19px; left: -21px;
  }

  .li_win {
    background-image: url('@/../public/win_icon.svg');
  }
  .li_nom {
    background-image: url('@/../public/nom_icon.svg');
  }
  .li_unofficial_nom {
    background-image: url('@/../public/unofficial_nom_icon.svg');
  }

  html[data-bs-theme=dark] {
    .li_win {
      background-image: url('@/../public/win_icon-gold.svg');
    }
    .li_nom {
      background-image: url('@/../public/nom_icon-gold.svg');
    }
    .li_unofficial_nom {
      background-image: url('@/../public/unofficial_nom_icon-dark.svg');
    }
  }

  .mol-watermark {
    z-index: 1;
    opacity: 5%;
    color: black;
    mix-blend-mode: multiply;

    position: fixed;
    bottom: 20px;
    right: 20px;

    pointer-events: none;
    user-select: none;
    font-size: 50px;
    font-weight: bold;
    text-transform:uppercase;
    text-align: center;
    line-height: 85%;

    border-style: solid;
    border-width: 5px;
    border-radius: 15px;

    padding: 0.1em;
  }

  [data-bs-theme=dark] .mol-watermark {
    color: white;
    mix-blend-mode: lighten;
  }

  .no-wrap {
    white-space: nowrap;
  }

  .hide-unless-hovering {
    opacity: 0;
    &:hover {
      opacity: revert;
    }
  }


</style>
